import WifiIcon from '@mui/icons-material/Wifi'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import {CircularProgress, Tooltip} from '@mui/material'
import {Box, Checkbox} from '@viptech/react-components'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import CheckboxTable from 'src/components/CheckboxTable'
import LinkWithAction from 'src/components/LinkWithAction'
import RowHover from 'src/components/RowHover/RowHover'
import {DataTablePagination} from 'src/components/data-table-pagination'
import Table from 'src/components/table/Table'
import TableData from 'src/components/table/components/tableData/TableData'
import {TableHeaderColumn, TableHeaderRow} from 'src/components/table/components/tableHeader/TableHeaderBase'
import {useCompany} from 'src/contexts/CompanyContext'
import {Server} from 'src/services/api'
import {Page} from 'src/types'
import {RenderIfConditionIsMet} from 'src/utilities/RenderIfConditionIsMet'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {ServerTableOption} from './ServerTableOption'
type ServerGridTableProps = {
  servers: Server[]
  loadingServers: boolean
  totalElements: number
  serversPage: Page
  setServersPage: Function
  openEditModal: (server: Server) => void
  selectedServerIds: number[]
  setSelectedServerIds: (ids: number[]) => void
  serverStatus: {[x: number]: {status: 'OK' | 'ERROR'; authStatus: 'OK' | 'ERROR'}}
  fetchError: boolean
  confirmBeforeDelete: (message: string, title: string) => void
}

export default function ServerGridTable(props: ServerGridTableProps) {
  const {
    servers,
    loadingServers,
    totalElements,
    serversPage,
    setServersPage,
    openEditModal,
    selectedServerIds,
    setSelectedServerIds,
    serverStatus,
    fetchError,
    confirmBeforeDelete,
  } = props
  const hasUpdateServersPermission = ValidateUserPermissions('servers:update')
  const hasDeleteServersPermission = ValidateUserPermissions('servers:delete')
  const serversPageChange = (page: number) => setServersPage((lastPage: Page) => ({...lastPage, page}))
  const serversPageSizeChange = (pageSize: number) => setServersPage((lastPage: Page) => ({...lastPage, pageSize}))
  const {getCompanyName} = useCompany()

  const tableControl = {
    pageIndex: serversPage.page,
    pageSize: serversPage.pageSize,
    total: totalElements,
    setPageSize: serversPageSizeChange,
    setPageIndex: serversPageChange,
    nextPage: () => {
      serversPageChange(serversPage.page + 1)
    },
    previousPage: () => {
      serversPageChange(serversPage.page - 1)
    },
  }

  const columns = [
    {
      id: 'checkbox',
      name: '',
    },
    {
      id: 'status',
      name: '',
    },
    {
      id: 'name',
      name: 'Nome da Integração',
    },
    {
      id: 'ip',
      name: 'Host',
    },
    {
      id: 'company',
      name: 'Empresa',
    },
    {
      id: 'serverType',
      name: 'Servidor',
    },
    {
      id: 'self',
      name: '',
    },
  ]

  if (!hasDeleteServersPermission) {
    columns.shift()
  }

  function handleOpenEditModal(server: Server) {
    if (!hasUpdateServersPermission) return
    openEditModal(server)
  }

  const handleDeleteServer = (server: Server) => {
    setSelectedServerIds([server.id])
    confirmBeforeDelete(`Deletar servidor`, `Será removido o servidor e tudo que está vinculado a ele. A ação não poderá ser desfeita.`)
  }

  const onSelectAll = () => {
    if (selectedServerIds.length) return setSelectedServerIds([])
    const allIds = servers.map((row) => row.id)
    setSelectedServerIds(allIds)
  }

  function renderServerStatus(server: Server) {
    const loading = (
      <Box style={{paddingTop: '2px'}}>
        <CircularProgress size={18} />
      </Box>
    )
    if (fetchError) return ''
    if (!serverStatus) return loading
    const statusRequest = serverStatus[server.id]
    if (!statusRequest) return loading
    if (statusRequest.status === 'OK') {
      return (
        <Tooltip title='Servidor Online' placement='top'>
          <Box>🟢</Box>
        </Tooltip>
      )
    }
    return (
      <Tooltip title='Servidor Offline' placement='top'>
        <Box>🔴</Box>
      </Tooltip>
    )
  }

  function renderServerAuthStatus(server: Server) {
    const loading = (
      <Box style={{paddingTop: '2px'}}>
        <CircularProgress size={18} />
      </Box>
    )

    if (fetchError) return ''
    if (!serverStatus) return loading

    const statusRequest = serverStatus[server.id]
    if (!statusRequest) return loading

    if (statusRequest.authStatus === 'OK') {
      return (
        <Tooltip title='Autenticação OK' placement='top'>
          <Box>
            <WifiIcon />
          </Box>
        </Tooltip>
      )
    }

    return (
      <Tooltip title='Erro na autenticação' placement='top'>
        <Box>
          <WifiOffIcon />
        </Box>
      </Tooltip>
    )
  }

  const customHeader = (
    <thead>
      <TableHeaderRow>
        {columns.map((col) =>
          col.id === 'checkbox' ? (
            <TableHeaderColumn>
              <div style={{paddingLeft: '10px'}}>
                <Checkbox id='check' checked={selectedServerIds.length ? servers.length === selectedServerIds.length : false} onChange={onSelectAll} />
              </div>
            </TableHeaderColumn>
          ) : (
            <TableHeaderColumn>{col.name}</TableHeaderColumn>
          ),
        )}
      </TableHeaderRow>
    </thead>
  )
  return (
    <Box style={styles.gridItem}>
      <Box flex={1} display={'flex'}>
        <Table skeletonRowsAmount={20} columns={columns} isLoading={loadingServers} tableHeaderChildren={customHeader}>
          {servers.map((server) => (
            <RowHover>
              <RenderIfConditionIsMet condition={hasDeleteServersPermission}>
                <TableData width='20px' paddingLeft='10px'>
                  <CheckboxTable id={server.id} selectedIds={selectedServerIds} setSelectedIds={setSelectedServerIds} />
                </TableData>
              </RenderIfConditionIsMet>
              <TableData width='20px'>
                <Box display='flex' align='center' justify='center' columnGap='5px'>
                  {renderServerStatus(server)}
                  {renderServerAuthStatus(server)}
                </Box>
              </TableData>
              <TableData>
                <LinkWithAction onClick={() => handleOpenEditModal(server)}>{server.name}</LinkWithAction>
              </TableData>
              <TableData>{server.ip}</TableData>
              <TableData>{getCompanyName(server?.companyId)}</TableData>
              <TableData>{server.serverType?.description}</TableData>
              <TableData>
                <ServerTableOption
                  onClick={(event) => {
                    if (event === 'edit') openEditModal(server)
                    if (event === 'delete') handleDeleteServer(server)
                  }}
                  hasUpdatePermission={hasUpdateServersPermission}
                  hasDeletePermission={hasDeleteServersPermission}
                />
              </TableData>
            </RowHover>
          ))}
        </Table>
      </Box>

      {!loadingServers && servers.length === 0 ? (
        <Box style={styles.noRegisters}>Nenhum registro encontrado</Box>
      ) : (
        <Box style={styles.pagination}>
          <DataTablePagination table={tableControl} />
        </Box>
      )}
    </Box>
  )
}

const styles = createStyleSheet({
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: '0px 20px',
    height: '100%',
    minHeight: '79vh',
  },
  noRegisters: {
    fontFamily: "'Inter', 'sans-serif'",
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 450,
    minHeight: '65vh',
    '@media(max-width:1540px)': {
      fontSize: '0.95em',
    },
  },
  pagination: {
    display: 'flex',
    margin: '15px 0',
  },
})
